import React from 'react';
import { Link } from 'react-router-dom';
import LazyImage from './LazyImage';
import './BenefitCard.css';

// Show the "NUEVO" badge only for benefits added within this window.
const NEW_BADGE_THRESHOLD_DAYS = 30;

const getInitials = (partner) => {
  if (!partner) return '?';
  const words = partner.trim().split(/\s+/);
  return words
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join('');
};

const isNew = (createdAt) => {
  if (!createdAt) return false;
  const created = new Date(createdAt);
  if (Number.isNaN(created.getTime())) return false;
  const ageInDays = (Date.now() - created.getTime()) / (1000 * 60 * 60 * 24);
  return ageInDays >= 0 && ageInDays <= NEW_BADGE_THRESHOLD_DAYS;
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return null;
  // Force UTC so a date-only string like "2024-12-31" doesn't shift a day
  // backward in timezones behind UTC.
  return date.toLocaleDateString('es-PE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

const formatValidity = (startDate, endDate) => {
  const end = endDate ? formatDate(endDate) : null;
  if (!end) return null;
  const start = startDate ? formatDate(startDate) : null;
  return start ? `Válido del ${start} al ${end}` : `Válido hasta el ${end}`;
};

const formatDiscountHeadline = (benefit) => {
  if (benefit.discount_pct && benefit.discount_pct > 0) {
    return `${benefit.discount_pct}% OFF`;
  }
  if (benefit.discount_price && benefit.list_price) {
    return `Antes $${benefit.list_price}`;
  }
  return null;
};

const BenefitCard = React.memo(({ benefit }) => {
  const validity = formatValidity(benefit.start_date, benefit.end_date);
  const discountHeadline = formatDiscountHeadline(benefit);

  return (
    <Link
      to={`/beneficios/${benefit.slug}`}
      className="benefit-card"
      aria-label={`Ver beneficio: ${benefit.title}`}
    >
      <div className="benefit-card__image-wrapper">
        <LazyImage
          src={benefit.image_cover}
          alt={`Imagen de ${benefit.title}`}
          className="benefit-image"
        />
        {benefit.category && (
          <span className="benefit-card__category-chip">{benefit.category.name}</span>
        )}
        {isNew(benefit.created_at) && (
          <span className="benefit-card__new-badge">NUEVO</span>
        )}
      </div>
      <div className="benefit-card__logo-chip">
        {benefit.logo ? (
          <LazyImage
            src={benefit.logo}
            alt={`Logo de ${benefit.partner}`}
            className="benefit-card__logo-img"
          />
        ) : (
          <div className="benefit-card__logo-initials">{getInitials(benefit.partner)}</div>
        )}
      </div>
      <div className="benefit-card__content">
        {benefit.partner && (
          <p className="benefit-card__partner">{benefit.partner}</p>
        )}
        {discountHeadline && (
          <p className="benefit-card__discount-headline">{discountHeadline}</p>
        )}
        {validity && <p className="benefit-card__validity">{validity}</p>}
        <span className="benefit-card__cta-text">Ver beneficio →</span>
      </div>
    </Link>
  );
});

BenefitCard.displayName = 'BenefitCard';

export default BenefitCard;
